import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import supabase from "@/lib/supabase";
import { verifyAdminPassword } from "@/lib/verifyAdminPassword";
import * as XLSX from "xlsx";

interface Cohort {
  id: number;
  name: string;
  created_at: string;
}

interface Application {
  id: number;
  cohort_id: number;
  name: string;
  phone: string;
  email: string;
  level: string;
  schedule: string;
  age_group: string;
  goals: string;
  referral: string;
  message: string;
  created_at: string;
}

type AuthStep = "password" | "dashboard";

export default function AdminDashboard() {
  const [step, setStep] = useState<AuthStep>(() => {
    return sessionStorage.getItem("admin_authed") === "true" ? "dashboard" : "password";
  });
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordChecking, setPasswordChecking] = useState(false);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [activeCohortId, setActiveCohortId] = useState<number | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch cohorts on mount
  useEffect(() => {
    if (step !== "dashboard") return;
    const fetchCohorts = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from("cohorts")
          .select("*")
          .order("created_at", { ascending: true });
        if (fetchError) throw fetchError;
        const list = data as Cohort[];
        setCohorts(list);
        if (list.length > 0) {
          // Try to read active cohort from localStorage first
          const stored = localStorage.getItem("active_cohort_id");
          const storedId = stored ? Number(stored) : null;
          const found = storedId ? list.find((c) => c.id === storedId) : null;
          setActiveCohortId(found ? found.id : list[0].id);
        }
      } catch {
        setError("기수 데이터를 불러오지 못했습니다.");
      }
    };
    fetchCohorts();
  }, [step]);

  // Fetch applications when cohort changes
  useEffect(() => {
    if (!activeCohortId) return;
    const fetchApps = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from("applications")
          .select("*")
          .eq("cohort_id", activeCohortId)
          .order("created_at", { ascending: false });
        if (fetchError) throw fetchError;
        setApplications(data as Application[]);
      } catch {
        setError("신청 데이터를 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
  }, [activeCohortId]);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordChecking(true);
    setPasswordError(false);
    try {
      const trimmed = passwordInput.trim();
      if (!trimmed) {
        setPasswordError(true);
        return;
      }
      const valid = await verifyAdminPassword(trimmed);
      if (!valid) {
        setPasswordError(true);
        setPasswordInput("");
      } else {
        sessionStorage.setItem("admin_authed", "true");
        setStep("dashboard");
        setPasswordInput("");
        setPasswordError(false);
      }
    } catch {
      setPasswordError(true);
      setPasswordInput("");
    } finally {
      setPasswordChecking(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_authed");
    setStep("password");
    setPasswordInput("");
    setPasswordError(false);
  };

  // CSV download
  const handleDownloadCSV = () => {
    const headers = [
      "No.",
      "이름",
      "연락처",
      "이메일",
      "수준",
      "희망 시간",
      "연령",
      "목표",
      "유입 경로",
      "문의 내용",
      "신청일",
    ];

    const escapeCsv = (val: string): string => {
      const sanitized = val.replace(/"/g, '""');
      return `"${sanitized}"`;
    };

    const rows = applications.map((app, idx) => [
      applications.length - idx,
      escapeCsv(app.name),
      escapeCsv(app.phone),
      escapeCsv(app.email),
      escapeCsv(app.level),
      escapeCsv(app.schedule || ""),
      escapeCsv(app.age_group || ""),
      escapeCsv(app.goals || ""),
      escapeCsv(app.referral || ""),
      escapeCsv(app.message || ""),
      new Date(app.created_at).toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const cohortName = activeCohort?.name?.replace(/\s+/g, "_") || "unknown";
    link.href = url;
    link.download = `신청자목록_${cohortName}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Excel download (xlsx via SheetJS)
  const handleDownloadExcel = () => {
    const headers = [
      "No.",
      "이름",
      "연락처",
      "이메일",
      "수준",
      "희망 시간",
      "연령",
      "목표",
      "유입 경로",
      "문의 내용",
      "신청일",
    ];

    const rows = applications.map((app, idx) => [
      applications.length - idx,
      app.name,
      app.phone,
      app.email,
      app.level,
      app.schedule || "",
      app.age_group || "",
      app.goals || "",
      app.referral || "",
      app.message || "",
      new Date(app.created_at).toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    ]);

    const sheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    // Auto-fit column widths
    const colWidths = headers.map((h, colIdx) => {
      const maxLen = sheetData.reduce((max, row) => {
        const cellLen = String(row[colIdx] ?? "").length;
        return Math.max(max, cellLen);
      }, h.length);
      return { wch: Math.min(maxLen + 4, 40) };
    });
    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "신청자 목록");

    const cohortName = activeCohort?.name?.replace(/\s+/g, "_") || "unknown";
    XLSX.writeFile(
      workbook,
      `신청자목록_${cohortName}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // Stats
  const totalApps = applications.length;
  const scheduleStats = applications.reduce<Record<string, number>>((acc, app) => {
    const key = app.schedule || "미지정";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const levelStats = applications.reduce<Record<string, number>>((acc, app) => {
    const key = app.level || "미지정";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const ageStats = applications.reduce<Record<string, number>>((acc, app) => {
    const key = app.age_group || "미지정";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const activeCohort = cohorts.find((c) => c.id === activeCohortId);

  // Password gate
  if (step === "password") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="bg-background-50 rounded-xl border border-background-200 p-8 w-full max-w-sm mx-4">
          <div className="text-center mb-6">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-500 text-background-50 font-heading text-2xl mb-3">
              日
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-950">관리자 대시보드</h2>
            <p className="text-sm text-foreground-600 mt-1">비밀번호를 입력해 주세요</p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-3">
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => {
                setPasswordInput(e.target.value);
                setPasswordError(false);
              }}
              placeholder="비밀번호"
              autoFocus
              className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900"
            />
            {passwordError && (
              <p className="text-xs text-primary-600">비밀번호가 일치하지 않습니다</p>
            )}
            <button
              type="submit"
              disabled={passwordChecking}
              className="w-full px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
            >
              {passwordChecking ? "확인 중..." : "대시보드 열기"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-500 text-background-50 font-heading text-lg hover:bg-primary-600 transition-colors"
              aria-label="메인 페이지로 이동"
            >
              日
            </Link>
            <h1 className="font-heading text-lg font-bold text-foreground-950">관리자 대시보드</h1>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="text-xs text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
            >
              사이트로 이동
            </a>
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              로그아웃
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Cohort selector */}
        <div className="mb-8">
          <label className="block text-xs font-semibold text-foreground-600 mb-2">기수 선택</label>
          <div className="flex flex-wrap gap-2">
            {cohorts.map((c) => {
              const isActive = c.id === activeCohortId;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveCohortId(c.id)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors whitespace-nowrap cursor-pointer ${
                    isActive
                      ? "bg-primary-500 border-primary-500 text-background-50"
                      : "bg-background-50 border-background-300 text-foreground-800 hover:border-primary-300"
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 rounded-xl bg-background-100 border border-background-200">
            <p className="text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-1">
              총 신청자
            </p>
            <p className="font-heading text-3xl font-bold text-foreground-950">{totalApps}</p>
            {activeCohort && (
              <p className="mt-1 text-xs text-foreground-500">{activeCohort.name} 기준</p>
            )}
          </div>

          <div className="p-5 rounded-xl bg-background-100 border border-background-200">
            <p className="text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-2">
              시간대별
            </p>
            <div className="space-y-1">
              {Object.entries(scheduleStats).length === 0 ? (
                <p className="text-sm text-foreground-400">데이터 없음</p>
              ) : (
                Object.entries(scheduleStats)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 3)
                  .map(([key, count]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-foreground-700 truncate mr-2">{key}</span>
                      <span className="font-semibold text-foreground-950 whitespace-nowrap">{count}명</span>
                    </div>
                  ))
              )}
            </div>
          </div>

          <div className="p-5 rounded-xl bg-background-100 border border-background-200">
            <p className="text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-2">
              레벨별
            </p>
            <div className="space-y-1">
              {Object.entries(levelStats).length === 0 ? (
                <p className="text-sm text-foreground-400">데이터 없음</p>
              ) : (
                Object.entries(levelStats)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 3)
                  .map(([key, count]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-foreground-700 truncate mr-2">{key}</span>
                      <span className="font-semibold text-foreground-950 whitespace-nowrap">{count}명</span>
                    </div>
                  ))
              )}
            </div>
          </div>

          <div className="p-5 rounded-xl bg-background-100 border border-background-200">
            <p className="text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-2">
              연령별
            </p>
            <div className="space-y-1">
              {Object.entries(ageStats).length === 0 ? (
                <p className="text-sm text-foreground-400">데이터 없음</p>
              ) : (
                Object.entries(ageStats)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 3)
                  .map(([key, count]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-foreground-700 truncate mr-2">{key}</span>
                      <span className="font-semibold text-foreground-950 whitespace-nowrap">{count}명</span>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>

        {/* Applications table */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              신청자 목록
              {!loading && (
                <span className="ml-2 text-sm font-normal text-foreground-500">
                  {totalApps}명
                </span>
              )}
            </h2>
            {applications.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownloadCSV}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-secondary-500 hover:bg-secondary-600 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-download-line text-sm"></i>
                  CSV
                </button>
                <button
                  onClick={handleDownloadExcel}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-file-excel-2-line text-sm"></i>
                  Excel
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-foreground-600 text-sm mb-3">{error}</p>
              <button
                onClick={() => setActiveCohortId(activeCohortId)}
                className="px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
              >
                다시 시도
              </button>
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-20 bg-background-100 rounded-xl border border-background-200">
              <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block"></i>
              <p className="text-foreground-500 text-sm">아직 신청 내역이 없습니다</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-background-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100 border-b border-background-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      No.
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      이름
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      연락처
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      이메일
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      수준
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      희망 시간
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      연령
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      목표
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      유입 경로
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider whitespace-nowrap">
                      신청일
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app, idx) => (
                    <tr
                      key={app.id}
                      className={`border-b border-background-100 hover:bg-background-50 transition-colors ${
                        idx % 2 === 0 ? "bg-background-50" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-3 text-foreground-400 text-xs whitespace-nowrap">
                        {applications.length - idx}
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground-900 whitespace-nowrap">
                        {app.name}
                      </td>
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">
                        {app.phone}
                      </td>
                      <td className="px-4 py-3 text-foreground-700">
                        <span className="text-xs">{app.email}</span>
                      </td>
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">
                        <span className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-xs">
                          {app.level}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap text-xs">
                        {app.schedule}
                      </td>
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap text-xs">
                        {app.age_group}
                      </td>
                      <td className="px-4 py-3 text-foreground-700 max-w-[180px]">
                        <span className="text-xs line-clamp-2">{app.goals}</span>
                      </td>
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap text-xs">
                        {app.referral || "-"}
                      </td>
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs">
                        {new Date(app.created_at).toLocaleDateString("ko-KR", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Expandable message section */}
          {applications.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="font-heading text-sm font-bold text-foreground-950">
                문의 내용
              </h3>
              {applications
                .filter((app) => app.message && app.message.trim())
                .map((app) => (
                  <div
                    key={`msg-${app.id}`}
                    className="p-4 rounded-lg bg-background-100 border border-background-200"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-semibold text-foreground-900">{app.name}</span>
                      <span className="text-xs text-foreground-400">
                        {new Date(app.created_at).toLocaleDateString("ko-KR", {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{app.message}</p>
                  </div>
                ))}
              {applications.filter((app) => app.message && app.message.trim()).length === 0 && (
                <p className="text-sm text-foreground-400">문의 내용이 있는 신청이 없습니다.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}